import { useState } from "react";
import { DEFAULT_THRESHOLD_DAYS } from "./api";
import { apiRequest } from "./apiRequest";
import { showToast } from "./toastOptions";

/**
 * Shared hook for threshold-days state + update logic.
 *
 * @param {Object} opts
 * @param {"company"|"project"} opts.variant - determines API path
 * @param {Function} [opts.onUpdated] - optional callback after successful update
 * @returns threshold state, handlers, and loading flag
 */
export function useThresholdDays({ variant = "company", onUpdated } = {}) {
  const [thresholdDays, setThresholdDays] = useState(null);
  const [inputDays, setInputDays] = useState(DEFAULT_THRESHOLD_DAYS);
  const [showConfirm, setShowConfirm] = useState(false);
  const [updating, setUpdating] = useState(false);

  const handleInputChange = (e) => setInputDays(Number.parseInt(e.target.value, 10) || 0);
  const handleSubmit = () => setShowConfirm(true);
  const handleCancel = () => setShowConfirm(false);

  /**
   * @param {Object} entity - company or project object
   *   For company: { id, name }
   *   For project: { id, name, company: { id, name } }
   */
  const updateThresholdDays = async (entity) => {
    if (updating) return;
    setUpdating(true);
    setShowConfirm(false);
    try {
      if (variant === "company") {
        await apiRequest(
          `${process.env.REACT_APP_API_BASE_URL}/companies/${entity.id}/inactive-days-threshold/`,
          {
            method: "POST",
            body: {
              company_id: entity.id,
              company_name: entity.name,
              inactive_days_threshold: inputDays,
            },
          }
        );
      } else {
        await apiRequest(
          `${process.env.REACT_APP_API_BASE_URL}/projects/${entity.id}/inactive-days-threshold/`,
          {
            method: "POST",
            body: {
              inactive_days_threshold: inputDays,
              company_id: entity.company.id,
              project_id: entity.id,
              project_name: entity.name,
              company_name: entity.company.name,
            },
          }
        );
      }
      setThresholdDays(inputDays);
      showToast(
        "success",
        variant === "company"
          ? "Successfully updated default inactive days threshold"
          : `Successfully updated threshold days to ${inputDays} days`
      );
      onUpdated?.();
    } catch (e) {
      showToast("error", e.message || "Failed to update threshold");
      setInputDays(thresholdDays ?? DEFAULT_THRESHOLD_DAYS);
    } finally {
      setUpdating(false);
    }
  };

  return {
    thresholdDays,
    setThresholdDays,
    inputDays,
    setInputDays,
    showConfirm,
    handleInputChange,
    handleSubmit,
    handleCancel,
    updateThresholdDays,
    updating,
  };
}
